export const CANONICAL_BASE =
  import.meta.env.VITE_CANONICAL_BASE || 'https://plevits.com';

export const SITE_NAME = 'Plevits';
export const DEFAULT_OG_IMAGE = '/og-image.png';
export const DEFAULT_LOCALE = 'en';

export interface PageSEO {
  title: string;
  description: string;
  noIndex?: boolean;
  skipSuffix?: boolean;
}

export const PAGE_SEO: Record<string, PageSEO> = {
  '/': {
    title: 'SaaS Health Score & Metrics Dashboard — MRR, Churn & Valuation from Stripe',
    description:
      'Connect Stripe and instantly see your SaaS health score, MRR breakdown, churn rate, net revenue retention, and estimated valuation. Free for founders.',
    skipSuffix: true,
  },
  '/pricing': {
    title: 'SaaS Analytics Pricing — Free MRR Tracker & Pro Health Score Plans',
    description:
      'Compare Plevits free and pro plans. SaaS health analytics with MRR tracking, churn analysis, ARR, NRR, health scores, and valuation estimates.',
  },
  '/directory': {
    title: 'SaaS Company Directory — Compare MRR, Growth & Health Scores',
    description:
      'Browse SaaS companies with verified revenue metrics from Stripe. Compare MRR, ARR, growth rate, churn, and health scores across the directory.',
  },
  '/login': {
    title: 'Sign In to Your Dashboard',
    description: '',
    noIndex: true,
  },
  '/signup': {
    title: 'Get Started Free — Connect Stripe in 2 Minutes',
    description:
      'Create your Plevits account and connect Stripe read-only to get instant SaaS health analytics. No credit card required for the free plan.',
  },
  '/forgot-password': {
    title: 'Reset Your Password',
    description: '',
    noIndex: true,
  },
  '/example-profile': {
    title: 'Example SaaS Profile',
    description: '',
    noIndex: true,
  },
  '/terms': {
    title: 'Terms of Service',
    description:
      'Read the Plevits terms of service covering account usage, data handling, subscription billing, and platform responsibilities for SaaS analytics.',
  },
  '/privacy': {
    title: 'Privacy Policy — How We Handle Your Stripe Data',
    description:
      'Learn how Plevits protects your data with read-only Stripe access, AES-256 encryption, and GDPR-compliant processing. No data is sold or shared.',
  },
  '/cookies': {
    title: 'Cookie Policy',
    description:
      'Plevits cookie policy explaining which cookies are used for authentication, analytics, and preferences on the SaaS health analytics platform.',
  },
  '/disclaimer': {
    title: 'Disclaimer — Valuation Estimates & Metrics',
    description:
      'Important disclaimers about Plevits SaaS valuation estimates, health scores, and metrics. These are indicative tools, not financial advice.',
  },
  '/security': {
    title: 'Security — Encryption, Access Controls & Compliance',
    description:
      'How Plevits secures your Stripe data: read-only OAuth, AES-256 encryption at rest, TLS in transit, SOC 2-aligned practices, and access controls.',
  },
  '/saas-metrics': {
    title: 'SaaS Metrics: The Complete Guide to KPIs That Matter',
    description:
      'Learn the SaaS metrics that drive growth and valuation — MRR, ARR, churn rate, NRR, and health score. Formulas, benchmarks, and how to track them.',
  },
  '/saas-metrics/mrr': {
    title: 'MRR (Monthly Recurring Revenue) — Definition, Formula & Breakdown',
    description:
      'What is MRR? Learn how to calculate Monthly Recurring Revenue, understand MRR breakdown components (new, expansion, churn), and see growth benchmarks for SaaS.',
  },
  '/saas-metrics/churn-rate': {
    title: 'SaaS Churn Rate — How to Calculate, Benchmark & Reduce It',
    description:
      'Calculate customer and revenue churn rate for SaaS. Includes formulas, benchmarks by segment (SMB, mid-market, enterprise), and reduction strategies.',
  },
  '/saas-metrics/net-revenue-retention': {
    title: 'Net Revenue Retention (NRR) — Formula, Benchmarks & Why It Predicts Growth',
    description:
      'NRR measures expansion vs churn from existing customers. Learn the NRR formula, what good NRR looks like (110%+), and how to achieve net negative churn.',
  },
  '/saas-metrics/arr': {
    title: 'ARR (Annual Recurring Revenue) — Definition, Formula & ARR vs MRR',
    description:
      'What is ARR? Learn the ARR formula (MRR × 12), when to use ARR vs MRR, key ARR milestones ($1M, $10M, $100M), and how ARR drives SaaS valuation.',
  },
  '/saas-metrics/saas-valuation': {
    title: 'SaaS Valuation — Methods, ARR Multiples & What Drives Your Multiple',
    description:
      'How to value a SaaS company using ARR multiples. Includes typical multiple ranges (3x–40x), the Rule of 40, and the metrics that increase your valuation.',
  },
  '/saas-metrics/health-score': {
    title: 'SaaS Health Score — What It Measures & How Plevits Calculates It',
    description:
      'A SaaS health score combines growth, churn, stability, concentration, and maturity into a 0–100 score. Learn how Plevits calculates it and how to improve yours.',
  },
};
